import React, { useState, useEffect, useRef } from "react";

import {
  Drawer,
  Card,
  Form,
  Row,
  Col,
  Input,
  Typography,
  Table,
  Space,
  Button,
  Timeline,
  message,
  Descriptions
} from "antd"

import useModalCloseConfirm from "hooks/base/modalCloseConfirm";
import { API_VEHICLE_UPLOAD_PHOTO } from "hooks/carRental/_";
import { useCSOrderListTemplate } from "hooks/notification/csOrderList";
import { useOrderList } from "hooks/order-management/order-list";
import SPUploadPhotos from "components/base/SPUploadPhotos";
import SPUploadSinglePhoto from "components/base/SPUploadSinglePhoto";
import VideoPreview from "components/order-management/base/VideoPreview";
import SPUpload from "components/base/SPUpload";
import Attachments from './Attachments'

import emiter, { _EVENTS } from "../../../../events";
import {changeEnumToText} from "../../../../utils/tool";
import EnumKeyToText from "../../../product-price/base/EnumKeyToText";
import SPButton from "../../../carRental/base/SPButton";

const { Text, Title } = Typography;
const { TextArea } = Input;

interface IComponentProps {
  enums?: any;
  permission?: any;
  onEdited?: any;
}

interface detailsInterface {
  order_code?: any;
  case_code?: any;
  case_status?: any;
  pick_store_code?: any;
  return_store_name?: any;
  case_title?: any;
  sessions?: any;
}

const SPDrawer: React.FC<IComponentProps> = (props) => {
  // 组件所需枚举值
  const { enums } = props;
  const [mainForm] = Form.useForm();
  const [isUploading, setIsUploading] = useState(false);

  // 工单详情
  const [details, setDetails] = useState<any>({});

  const columns: any[] = [
    {
      title: '操作时间',
      width: 150,
      dataIndex: 'create_time',
      key: 'create_time',
    },
    {
      title: "事件类型",
      width: 100,
      dataIndex: "case_status",
      key: "case_status",
      render: (_: any, record: any) => (
        <EnumKeyToText data={record} enums={enums} valueKey={'session_type'} nameSpace={'sessions_type'} />
      ),
    },
    {
      width: 200,
      title: '工单内容',
      dataIndex: 'content',
      key: 'content',
    },
    {
      width: 100,
      title: '操作人',
      dataIndex: 'operator',
      key: 'operator',
    },
    {
      width: 100,
      title: '发起人',
      dataIndex: 'sponsor',
      key: 'sponsor',
    },
    {
      title: '工单附件',
      key: 'content',
      dataIndex: 'content',
      render: (_: any, record: any) => (
        <Attachments data={record}></Attachments>
      )
    },
  ]

  const { visible, onOpen, onClose, onEdited, resetEditFlag } = useModalCloseConfirm({
    confirmText: "当前页面有未回复的内容，确认关闭吗？",
  });

  const { replySubmit, getDrawerDetail, refreshList } = useCSOrderListTemplate();

  const { tableActionView } = useOrderList();

  const drawerWidth = "90%";

  // 提取文件扩展名
  const getFileExtension = (url: string) => {
    const filename: any = url.split("/").pop();
    const extension = filename.split(".").pop().toLowerCase();
    return extension;
  };

  const mainFormFields = {
    text: {
      name: "session_content",
      label: "",
      required: false,
      placeholder: "请输入回复内容1000字内",
    },
    img: {
      name: "images",
      label: "上传图片（最多上传10张，≤10M/张，图片格式仅限.jpg、.png、.jpeg）",
      required: false,
    },
    video: {
      name: "resources",
      label: "上传视频（最多上传5个，视频格式仅限.mp4、.mov）",
      required: false,
    },
  };

  const handleValuesChange = () => {
    onEdited();
  };

  // 打开抽屉
  // 1. 请求详情接口，event-bus通过_EVENTS.NOTIFICATION.CS_ORDER_LIST.VIEW抛出details
  // 2. 打开弹窗
  const handleOpenDrawer = (data: any = {}) => {
    setDetails(data.details);
    onOpen();
  };

  // 确认关闭
  // 1. resetFields
  // 2. 清空页面数据
  const confirmClose = () => {
    setDetails({});
    mainForm.resetFields();
  };

  // 刷新抽屉内容
  const handleRefreshDrawer = (data: any = {}) => {
    setDetails(data.detail);
  };

  // 回复
  const onReply = () => {
    mainForm.validateFields().then(async (fieldsValue: any) => {
      // 1. 组合生成接口数据
      // 2. 调用hooks里面定义的回复方法
      // 3. 请求抽屉详情接口
      // 4. 回复成功才清空from，mainForm.resetFields
      const data = {
        session_type: 8,
        case_id: details.case_id,
        order_code: details.order_code,
        supplier_code: details.supplier_code,
        session_content: fieldsValue.session_content,
        images: Array.isArray(fieldsValue.images)
          ? fieldsValue.images.map((item: any) => item.file_addr)
          : [],
        videos: Array.isArray(fieldsValue.resources)
          ? fieldsValue.resources.map((item: any) => ({
              url: item.file_addr,
              type: getFileExtension(item.file_addr),
            }))
          : [],
      };
      try {
        await replySubmit(data);
        message.success("回复成功");
        mainForm.resetFields();
        resetEditFlag()
      } catch (err) {
        console.error("replySubmit error", err);
      }
      getDrawerDetail(details).then((res) => {
        setDetails(res?.data || {})
      });
      refreshList()
    }).catch((error) => {
      if (error?.errorFields?.length) {
        const firstErrorField = document.querySelector(
          `.ant-drawer-content-wrapper #${error.errorFields[0].name.join('-')}`
        );
        firstErrorField?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
  };

  useEffect(() => {
    emiter.on(_EVENTS.NOTIFICATION.CS_ORDER_LIST.VIEW, handleOpenDrawer);
    emiter.on(_EVENTS.NOTIFICATION.CS_ORDER_LIST.REFRESH, handleRefreshDrawer);
    emiter.on(_EVENTS.COMMON_EMIT.COMMON_UPLOAD_EMIT, setIsUploading);
    return () => {
      emiter.removeAllListeners(_EVENTS.NOTIFICATION.CS_ORDER_LIST.VIEW);
      emiter.removeAllListeners(_EVENTS.NOTIFICATION.CS_ORDER_LIST.REFRESH);
      emiter.removeAllListeners(_EVENTS.COMMON_EMIT.COMMON_UPLOAD_EMIT);
    };
  }, []);

  return (
    <>
      <Drawer
        title={
          <Space>
            <span>工单详情</span>
            <Text type="warning" style={{ fontSize: "14px" }}>
              温馨提示：若超过24小时未回复，系统将自动关闭工单，由平台进行处理，如产生相应判责类费用，由商家承担
            </Text>
          </Space>
        }
        width={drawerWidth}
        onClose={() => onClose({ callback: confirmClose })}
        open={visible}
        destroyOnClose
        footer={
          <Space style={{ display: "flex", justifyContent: "end" }}>
            <Button onClick={() => onClose({ callback: confirmClose })}>
              取消
            </Button>
            <Button type="primary"  disabled={details?.case_status === 3 || isUploading} onClick={onReply}>
              回复
            </Button>
          </Space>
        }
      >

        <Descriptions column={3} contentStyle={{ display: 'flex',alignItems: 'center', fontSize: '15px', fontWeight: '500' }} labelStyle={{ fontSize: "15px", fontWeight: "bold" }}>
          <Descriptions.Item label="工单编号">{details.case_id || "--"}</Descriptions.Item>
          <Descriptions.Item label="订单编号"><Text
            style={{color: 'var(--ant-primary-color)'}}
            type="danger"
            onClick={() => tableActionView(details)}
          >
            {details.order_code || "--"}
          </Text></Descriptions.Item>
          <Descriptions.Item label="工单状态">{changeEnumToText({
            nameSpace: 'case_status',
            value: details?.case_status,
            enums: enums
          }) || "--"}</Descriptions.Item>
          <Descriptions.Item label="工单类型">{details.case_title || "--"}</Descriptions.Item>
          <Descriptions.Item label="取车门店">
            {details.pickup_store_name || "--"}
          </Descriptions.Item>
          <Descriptions.Item label="还车门店">
            {details.return_store_name || "--"}
          </Descriptions.Item>
        </Descriptions>

        {/* 协同内容 */}
        <Card title="协同内容" style={{ marginBottom: 20 }}>
          <Table
            dataSource={details?.sessions || []}
            columns={columns}
            pagination={false}></Table>
        </Card>
        {/* 工单回复 */}
        <Card title="工单回复">
          <Form
            disabled={details?.case_status === 3}
            form={mainForm}
            layout="vertical"
            onValuesChange={handleValuesChange}
          >
            <Form.Item
              rules={[{ required: true, message: '请输入回复内容' }]}
              name={mainFormFields.text.name}
              label={mainFormFields.text.label}

            >
              <TextArea
                rows={4}
                showCount
                placeholder={mainFormFields.text.placeholder}
                maxLength={1000}
              />
            </Form.Item>
            <Form.Item
              name={mainFormFields.img.name}
              label={mainFormFields.img.label}
            >
              <SPUploadPhotos
                action={API_VEHICLE_UPLOAD_PHOTO}
                accept=".jpg,.jpeg,.png"
                maxNum={10}
                fileSize={10}
                hideTitle
              />
            </Form.Item>
            <Form.Item
              name={mainFormFields.video.name}
              label={mainFormFields.video.label}
            >
              <SPUpload
                setIsUploading={setIsUploading}
                accept=".mp4,.mov"
                fileType="video"
                maxNum={5}
                size={40}
                multiple
                action={API_VEHICLE_UPLOAD_PHOTO}
              />
            </Form.Item>
          </Form>
        </Card>
      </Drawer>
    </>
  );
};

export default SPDrawer;
